import { useState } from "react";
import { AiButton } from "../../../components/common/AIButton";
import SearchableSelect from "../../../components/common/SearchableSelect";
import AlertMessage from "../../../components/common/AlertMessage";
import { jobService } from "./jobService";
import { categoryApi } from "../../../api/categoryApi";
import JobManagementPage from "../../../Pages/JobManagementPage";

export default function GenerateImage() {
  const [loading, setLoading] = useState(false);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [showCategoryOptions, setShowCategoryOptions] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("success");
  const [refreshSignal, setRefreshSignal] = useState(0);

  const displayAlert = (type, text) => {
    setMessageType(type);
    setMessage(text);
  };

  const handleAutoGen = async () => {
    if (loading) {
      return;
    }

    setLoading(true);
    try {
      const job = await jobService.createJob("Auto-generate images", "GENERATE_IMAGE");
      if (job) {
        displayAlert("success", "Auto-generate job queued.");
        setRefreshSignal((prev) => prev + 1);
      } else {
        displayAlert("danger", "Failed to queue the auto-generate job.");
      }
    } catch (error) {
      displayAlert("danger", error?.message ?? "Failed to queue the auto-generate job.");
    } finally {
      setLoading(false);
    }
  };

  const handleAutoGenByCategory = async () => {
    if (!selectedCategory) {
      displayAlert("warning", "Select a category before creating a category job.");
      return;
    }

    if (categoryLoading) {
      return;
    }

    setCategoryLoading(true);
    try {
      const jobName = `Generate images: ${selectedCategory.label}`;
      const job = await jobService.createJob(jobName, "GENERATE_IMAGE_BY_CATEGORY", [selectedCategory.value]);
      if (job) {
        displayAlert("success", `Job queued for ${selectedCategory.label}.`);
        setRefreshSignal((prev) => prev + 1);
      } else {
        displayAlert("danger", "Failed to queue the category job.");
      }
    } catch (error) {
      displayAlert("danger", error?.message ?? "Failed to queue the category job.");
    } finally {
      setCategoryLoading(false);
    }
  };

  const fetchCategoryOptions = async (search = "") => {
    try {
      const params = {
        pageNumber: 1,
        pageSize: 10,
        filters: {},
        isSortDesc: false,
        sortKey: "createdAt",
        seachKey: search
      };
      const res = await categoryApi.getTable(params);
      if (!res || !res.success) {
        return [];
      }
      return (res.data?.items ?? []).map((item) => ({
        value: item.id,
        label: item.name
      }));
    } catch {
      return [];
    }
  };

  return (
    <div className="container">
      <h1 className="label">Auto Gen image for cards</h1>

      <div className="d-flex gap-2 align-items-center">
        <AiButton onClick={handleAutoGen} disabled={loading}>
          {loading ? "Queueing..." : "Auto Gen"}
        </AiButton>
        <button
          type="button"
          className="btn btn-sm btn-outline-primary"
          onClick={() => setShowCategoryOptions((prev) => !prev)}
        >
          {showCategoryOptions ? "Hide category option" : "Generate by category"}
        </button>
      </div>

      {showCategoryOptions && (
        <div className="card border mt-3 p-3 shadow-sm">
          <p className="mb-2 fw-semibold">Generate images by category</p>
          <div style={{ maxWidth: 320 }}>
            <SearchableSelect
              fetchApi={fetchCategoryOptions}
              value={selectedCategory}
              onChange={setSelectedCategory}
            />
          </div>
          <div className="mt-3">
            <AiButton
              onClick={handleAutoGenByCategory}
              disabled={categoryLoading || !selectedCategory}
            >
              {categoryLoading ? "Queueing..." : "Create job for category"}
            </AiButton>
          </div>
        </div>
      )}

      <div className="mt-3">
        <JobManagementPage refreshSignal={refreshSignal} />
      </div>

      <AlertMessage
        type={messageType}
        message={message}
        onClose={() => setMessage("")}
      />
    </div>
  );
}
